import { useState, useEffect } from "react";
import classNames from "classnames";
import styles from "./style.module.scss";

type Tab = {
  icon?: React.ReactNode;
  label: string;
  value: string;
  showDot?: boolean;
};

type CustomTabsProps = {
  tabs: Tab[];
  defaultActiveTab?: string;
  activeTab?: string;
  onTabChange?: (tabValue: string) => void;
  fullWidthTabs?: boolean;
  rightSideContent?: React.ReactNode;
};

const CustomTabs = ({
  tabs,
  defaultActiveTab,
  activeTab: controlledActiveTab,
  onTabChange,
  fullWidthTabs,
  rightSideContent,
}: CustomTabsProps) => {
  const [internalActiveTab, setInternalActiveTab] = useState(
    defaultActiveTab || tabs[0].value,
  );

  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  useEffect(() => {
    if (isControlled) return;
    if (defaultActiveTab) {
      setInternalActiveTab(defaultActiveTab);
    }
  }, [defaultActiveTab, isControlled]);

  const handleTabClick = (tabValue: string) => {
    if (!isControlled) {
      setInternalActiveTab(tabValue);
    }
    onTabChange?.(tabValue);
  };

  return (
    <div
      className={classNames(styles.tabsContainer, "w-full", {
        [styles.scrollableTabsContainer]: fullWidthTabs || rightSideContent,
      })}
    >
      <div
        className={classNames(styles.tabs, "flex items-center md:px-2.5", {
          "justify-between": fullWidthTabs || rightSideContent,
        })}
      >
        <div
          className={classNames(
            styles.tabsScrollWrapper,
            "flex items-center lg:gap-2.5 gap-1.5",
          )}
        >
          {tabs.map((tab) => (
            <button
              key={tab.value}
              className={classNames(
                styles.tab,
                activeTab === tab.value && styles.activeTab,
                "flex items-center sm:gap-2 gap-1",
                { "w-full": fullWidthTabs },
              )}
              onClick={() => handleTabClick(tab.value)}
            >
              {tab.icon ? (
                <span
                  style={{ position: "relative", display: "inline-flex" }}
                >
                  {tab.icon}
                  {tab.showDot && (
                    <span
                      aria-label="Unread messages"
                      style={{
                        position: "absolute",
                        top: -3,
                        right: -3,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#c23b23",
                        display: "inline-block",
                      }}
                    />
                  )}
                </span>
              ) : null}
              {tab.label}
              {!tab.icon && tab.showDot && (
                <span
                  aria-label="Unread messages"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#c23b23",
                    display: "inline-block",
                    marginLeft: 2,
                  }}
                />
              )}
            </button>
          ))}
        </div>
        {rightSideContent && (
          <div
            className={classNames(styles.rightSideWrapper, "flex items-center")}
          >
            {rightSideContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomTabs;
